import StyledButton from '@/components/styled/StyledButton';
import StyledContainer from '@/components/styled/StyledContainer';
import StyledListHeader from '@/components/styled/StyledListHeader';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import useTableControls from '@/hooks/useTableControls';
import { circuitsListParsers } from '@/utils/searchParams/parsers';
import { type CircuitsListUrlState } from '@/utils/searchParams/buildCircuitsListSearch';
import { RowSelectionState } from '@tanstack/react-table';
import { useQueryStates } from 'nuqs';
import { useEffect, useState } from 'react';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import SearchIcon from '@/assets/icons/search.svg?react';
import StyledTable from '@/components/styled/StyledTable';
import { circuitColumns } from '@/types/circuits/circuitColumnDef';
import StyledNextPreviousButtons from '@/components/styled/StyledNextPreviousButtons';
import { useForm } from 'react-hook-form';
import {
  useDeleteCircuit,
  useDeleteMultipleCircuits,
  useDuplicateCircuit,
  useGetCircuits,
  useGetCountriesWithCircuits,
  useGetDurationsWithCircuits,
} from '@/hooks/useCircuits';
import StyledSelect from '@/components/styled/StyledSelect';
import { COUNTRIES } from '../../../../../shared/CountriesData';
import StyledInput from '@/components/styled/StyledInput';
import ModalDeletion from '@/components/ui/ModalDeletion';
import ModalDuplication from '@/components/ui/ModalDuplication';
import { useNavigate } from 'react-router';
import { PATHS } from '@/constants/Paths';
import SelectAgenciesModal from '@/components/distributors/modals/SelectAgenciesModal';
import ShareIcon from '@/assets/icons/share-06.svg?react';
import { PAGE_SIZE } from '@/constants/pageSize';
import { CIRCUIT_DUPLICATION_FIELDS } from '@/constants/circuitDuplicationFields';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import Modal from '@/components/ui/Modal';
import { toast } from 'react-toastify';
import { toastOptions } from '@/hooks/useToast';
import {
  DeleteCircuitErrorCode,
  parseDeleteCircuitErrorCode,
} from '@/utils/circuits/deleteCircuitError';

type CircuitsListFormValues = {
  search: string;
  countryName: { value?: string; label?: string } | undefined;
  duration:
    | {
        value?: { durationDays: number; durationNights: number };
        label?: string;
      }
    | undefined;
};

function formValuesToUrl(values: CircuitsListFormValues): CircuitsListUrlState {
  return {
    name: values.search,
    countries: values.countryName?.value ?? null,
    durationDays: values.duration?.value?.durationDays.toString() ?? null,
    durationNights: values.duration?.value?.durationNights?.toString() ?? null,
    page: 1,
    sortField: '',
    sortOrder: 'asc',
  };
}

const ListCircuitsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const [urlFilters, setUrlFilters] = useQueryStates(circuitsListParsers);

  const { data: durationsOptions } = useGetDurationsWithCircuits((data) =>
    data?.map(({ durationDays, durationNights }) => ({
      value: { durationDays, durationNights },
      label: durationNights
        ? `${durationDays}j / ${durationNights}n`
        : `${durationDays}j`,
    })),
  );

  const formValues: CircuitsListFormValues = {
    search: urlFilters.name,
    countryName: urlFilters.countries
      ? { value: urlFilters.countries, label: urlFilters.countries }
      : undefined,
    duration: urlFilters.durationDays
      ? urlFilters.durationNights
        ? durationsOptions?.find(
            ({ value }) =>
              value.durationDays === Number(urlFilters.durationDays) &&
              (value.durationNights
                ? value.durationNights === Number(urlFilters.durationNights)
                : true),
          )
        : durationsOptions?.find(
            ({ value }) =>
              value.durationDays === Number(urlFilters.durationDays) &&
              !value.durationNights,
          )
      : undefined,
  };

  const { control, subscribe } = useForm<CircuitsListFormValues>({
    values: formValues,
  });

  useEffect(() => {
    const unsub = subscribe({
      formState: { values: true },
      callback: ({ values }) =>
        setUrlFilters({
          ...formValuesToUrl(values as CircuitsListFormValues),
          sortField: urlFilters.sortField,
          sortOrder: urlFilters.sortOrder,
        }),
    });
    return () => unsub();
  }, [subscribe, setUrlFilters, urlFilters.sortField, urlFilters.sortOrder]);

  const initialPage = urlFilters.page > 0 ? urlFilters.page : 1;
  const initialSorting = urlFilters.sortField
    ? [{ id: urlFilters.sortField, desc: urlFilters.sortOrder === 'desc' }]
    : [];
  const { page, setPage, sorting, setSorting, sortField, sortOrder } =
    useTableControls({
      initialPage,
      initialSorting,
      onPageChange: (nextPage) => {
        setUrlFilters({ page: nextPage > 0 ? nextPage : 1 });
      },
      onSortingChange: (nextSorting) => {
        const nextSort = nextSorting[0];
        setUrlFilters({
          page: 1,
          sortField: nextSort?.id ?? '',
          sortOrder: nextSort?.desc ? 'desc' : 'asc',
        });
      },
    });
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [circuitToDeleteIds, setCircuitToDeleteIds] = useState<string[]>([]);
  const [circuitToDuplicateId, setCircuitToDuplicateId] = useState<string>();
  const [circuitsToShareIds, setCircuitsToShareIds] = useState<string[]>([]);
  const [deleteErrorCode, setDeleteErrorCode] =
    useState<DeleteCircuitErrorCode | null>(null);

  const selectedCircuits = Object.keys(rowSelection);

  const debouncedName = useDebouncedSearch(urlFilters.name);

  const apiFilters = {
    name: debouncedName,
    countries: urlFilters.countries ?? '',
    durationDays: urlFilters.durationDays ?? '',
    durationNights: urlFilters.durationNights ?? '',
  };

  const { data: circuitsListResponse, isSuccess } = useGetCircuits({
    page,
    pageSize: PAGE_SIZE,
    sortField,
    sortOrder,
    filters: apiFilters,
  });

  const areAllSelectedCircuitsPublished = isSuccess
    ? selectedCircuits
        .map((circuitId) =>
          circuitsListResponse.circuits.find(
            (circuit) => circuit.id === circuitId,
          ),
        )
        .every((circuit) => circuit?.status === 'PUBLISHED')
    : false;

  const { data: countriesOptions } = useGetCountriesWithCircuits({
    select: (data) =>
      data?.map(({ country }) => ({ value: country, label: country })),
  });

  const { mutateAsync: deleteCircuit } = useDeleteCircuit();
  const { mutateAsync: deleteMultipleCircuits } = useDeleteMultipleCircuits();
  const { mutateAsync: duplicateCircuit } = useDuplicateCircuit();
  const openDeleteFlow = (circuitIds: string[]) => {
    const selectedCircuitRows =
      circuitsListResponse?.circuits.filter((circuit) =>
        circuitIds.includes(circuit.id),
      ) ?? [];

    const hasSharedCircuit = selectedCircuitRows.some(
      (circuit) => circuit._count.sharedCircuits > 0,
    );

    if (hasSharedCircuit) {
      setDeleteErrorCode('CIRCUIT_SHARED_DELETE_FORBIDDEN');
      setCircuitToDeleteIds([]);
      return;
    }

    setDeleteErrorCode(null);
    setCircuitToDeleteIds(circuitIds);
  };

  return (
    <StyledContainer>
      <StyledListHeader
        title={t('circuits.title')}
        subTitle={t('circuits.subtitle')}
      >
        {selectedCircuits.length > 0 && areAllSelectedCircuitsPublished && (
          <StyledButton
            title={t('circuits.shareSelected', {
              count: selectedCircuits.length,
            })}
            className="font-semibold gap-1 px-3 py-2 border-brand-300"
            onClick={() => setCircuitsToShareIds(selectedCircuits)}
            icon={
              <ShareIcon className="size-5 stroke-brand-600 fill-brand-600" />
            }
            variant="outline"
          />
        )}
        {selectedCircuits.length > 0 && (
          <StyledButton
            title={t('circuits.deleteSelected', {
              count: selectedCircuits.length,
            })}
            className="font-semibold gap-1 px-3 py-2"
            onClick={() => openDeleteFlow(selectedCircuits)}
            icon={<TrashIcon className="size-5 stroke-brand-600" />}
            variant="outlined"
          />
        )}
        <StyledButton
          title={t('circuits.addCircuit')}
          className="font-semibold gap-1 px-3 py-2"
          onClick={() => {
            navigate(PATHS.home.circuits.create, {
              state: { listSearchParams: urlFilters },
            });
          }}
          icon={<PlusIcon className="size-5 fill-white" />}
        />
      </StyledListHeader>
      <div className="flex flex-col w-full h-auto p-6 gap-6">
        <div className="flex flex-col gap-2">
          <span className="text-xs font-normal text-gray-600">
            {t('circuits.listFilters.title')}
          </span>
          <div className="flex flex-col md:flex-row justify-between gap-4">
            <div className="flex flex-col md:flex-row gap-4">
              <StyledSelect
                className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                control={control}
                options={countriesOptions ?? COUNTRIES}
                name="countryName"
                placeholder={t('circuits.listFilters.country')}
                isClearable
              />
              <StyledSelect
                className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                control={control}
                options={durationsOptions ?? []}
                name="duration"
                placeholder={t('circuits.listFilters.duration')}
                isClearable
              />
            </div>
            <div className="w-full md:w-1/3 lg:w-1/4 2xl:w-1/5">
              <StyledInput
                control={control}
                name="search"
                placeholder={t('circuits.listFilters.search')}
                icon={<SearchIcon className="size-5 stroke-gray-500" />}
              />
            </div>
          </div>
        </div>
        <StyledTable
          columns={getTranslatedConst(circuitColumns)}
          data={circuitsListResponse?.circuits ?? []}
          isLoading={!isSuccess}
          sorting={sorting}
          setSorting={setSorting}
          getRowId={(row) => row.id}
          setSelection={setRowSelection}
          onDeleteRow={(row) => {
            openDeleteFlow([row.original.id]);
          }}
          onDuplicateRow={(row) => {
            setCircuitToDuplicateId(row.original.id);
          }}
          onViewRow={(row) => {
            navigate(
              PATHS.home.circuits.view.replace(':circuitId', row.original.id),
              { state: { listSearchParams: urlFilters } },
            );
          }}
          onRowClick={(row) => {
            navigate(
              PATHS.home.circuits.view.replace(':circuitId', row.original.id),
              { state: { listSearchParams: urlFilters } },
            );
          }}
          onShareRow={(row) => {
            setCircuitsToShareIds([row.original.id]);
          }}
          shareEnablePredicate={(row) => row.original.status === 'PUBLISHED'}
        />
        {isSuccess && (
          <StyledNextPreviousButtons
            page={page}
            pageSize={PAGE_SIZE}
            setPage={setPage}
            dataLength={circuitsListResponse.total}
            label={t('circuits.circuit')}
          />
        )}
      </div>
      {circuitToDeleteIds.length > 0 && (
        <ModalDeletion
          closeModal={() => {
            setCircuitToDeleteIds([]);
          }}
          actionOnClick={() => {
            if (circuitToDeleteIds.length === 1) {
              deleteCircuit({ circuitId: circuitToDeleteIds[0] })
                .then(() => {
                  setCircuitToDeleteIds([]);
                  toast.success(t('trash.movedToTrash.circuit'), toastOptions);
                })
                .catch((error) => {
                  const errorCode = parseDeleteCircuitErrorCode(error);
                  if (errorCode) {
                    setCircuitToDeleteIds([]);
                    setDeleteErrorCode(errorCode);
                  }
                });
            } else if (selectedCircuits.length > 0) {
              deleteMultipleCircuits({ circuitIds: selectedCircuits })
                .then(() => {
                  setRowSelection({});
                  setCircuitToDeleteIds([]);
                  toast.success(t('trash.movedToTrash.circuits'), toastOptions);
                })
                .catch((error) => {
                  const errorCode = parseDeleteCircuitErrorCode(error);
                  if (errorCode) {
                    setCircuitToDeleteIds([]);
                    setDeleteErrorCode(errorCode);
                  }
                });
            }
          }}
          type="circuit"
          isMultiple={circuitToDeleteIds.length > 1}
        />
      )}
      {deleteErrorCode === 'CIRCUIT_SHARED_DELETE_FORBIDDEN' && (
        <Modal
          closeModal={() => setDeleteErrorCode(null)}
          title={t('circuits.deleteBlocked.shared.title')}
          message={t('circuits.deleteBlocked.shared.message')}
          actionTitle={t('common.close')}
          actionOnClick={() => setDeleteErrorCode(null)}
          hasAction={false}
          cancelBtnLabel={t('common.close')}
        />
      )}
      {deleteErrorCode === 'CIRCUIT_LINKED_TO_ESTIMATES_DELETE_FORBIDDEN' && (
        <Modal
          closeModal={() => setDeleteErrorCode(null)}
          title={t('circuits.deleteBlocked.estimates.title')}
          message={t('circuits.deleteBlocked.estimates.message')}
          actionTitle={t('common.close')}
          actionOnClick={() => setDeleteErrorCode(null)}
          hasAction={false}
          cancelBtnLabel={t('common.close')}
        />
      )}
      {circuitToDuplicateId && (
        <ModalDuplication
          actionOnClick={async (selectedFields) => {
            if (circuitToDuplicateId) {
              const duplicatedCircuit = await duplicateCircuit({
                circuitId: circuitToDuplicateId,
                fields: selectedFields.flatMap((fields) => fields.split(';')),
              });
              navigate(
                PATHS.home.circuits.view.replace(
                  ':circuitId',
                  duplicatedCircuit.id,
                ),
                { state: { listSearchParams: urlFilters } },
              );
            }
          }}
          closeModal={() => {
            setCircuitToDuplicateId(undefined);
          }}
          fields={getTranslatedConst(CIRCUIT_DUPLICATION_FIELDS)}
          fieldsTitle={t('circuits.viewForm.configuration')}
          otherFields={Array.from(
            Array(
              circuitsListResponse?.circuits.find(
                (circuit) => circuit.id === circuitToDuplicateId,
              )?.durationDays,
            ).keys(),
          ).map((day) => ({
            label: t('circuits.day_', { index: day + 1 }),
            value: `Jour ${day + 1}`,
          }))}
          otherFieldsTitle={t('circuits.days')}
          type="circuit"
          code={
            circuitsListResponse?.circuits.find(
              (circuit) => circuit.id === circuitToDuplicateId,
            )?.code ?? 0
          }
        />
      )}
      {circuitsToShareIds.length > 0 && (
        <SelectAgenciesModal
          closeModal={() => {
            setCircuitsToShareIds([]);
          }}
          circuitIds={circuitsToShareIds}
        />
      )}
    </StyledContainer>
  );
};

export default ListCircuitsPage;
